import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CurrencyRupeeIcon, 
  ShieldCheckIcon, 
  DocumentChartBarIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const Reports = () => {
  const navigate = useNavigate();

  const reportCategories = [
    {
      title: 'Statutory Reports',
      description: 'Government compliance and statutory reports',
      reports: [
        // {
        //   id: 'payroll-summary',
        //   title: 'Payroll Summary',
        //   description: 'Monthly payroll summaries and cost analysis',
        //   icon: ShieldCheckIcon,
        //   color: 'indigo',
        //   route: '/reports/payroll-summary',
        //   features: [
        //     'Monthly payroll costs',
        //     'Department-wise breakdown',
        //     'Earnings vs Deductions',
        //     'Employee cost analysis'
        //   ]
        // },
        {
          id: 'pf-report',
          title: 'PF Report',
          description: 'Employee & employer PF contributions with ECR file generation',
          icon: ShieldCheckIcon,
          color: 'blue',
          route: '/reports/pf',
          features: [
            'Employee contributions',
            'Employer contributions', 
            'ECR file generation',
            'Monthly PF statements'
          ]
        },
        {
          id: 'esi-report',
          title: 'ESI Report',
          description: 'Employee State Insurance contributions and file uploads',
          icon: BuildingOfficeIcon,
          color: 'green',
          route: '/reports/esi',
          features: [
            'Employee contributions',
            'Employer contributions',
            'Upload file format',
            'ESI statements'
          ]
        },
        // {
        //   id: 'employee-reports',
        //   title: 'Employee Reports',
        //   description: 'Individual employee salary and benefit reports',
        //   icon: DocumentChartBarIcon,
        //   color: 'orange',
        //   route: '/reports/employee-reports',
        //   features: [
        //     'Individual salary slips',
        //     'Annual salary statements',
        //     'Benefit summaries',
        //     'Leave and attendance'
        //   ]
        // },
        {
          id: 'pt-report',
          title: 'PT Report',
          description: 'Professional Tax deductions state-wise and payable amounts',
          icon: DocumentChartBarIcon,
          color: 'purple',
          route: '/reports/pt',
          features: [
            'State-wise PT deductions',
            'PT payable amounts',
            'Monthly PT statements',
            'Compliance tracking'
          ]
        },
        {
          id: 'tds-report',
          title: 'TDS Report',
          description: 'Tax Deducted at Source reports and compliance',
          icon: CurrencyRupeeIcon,
          color: 'orange',
          route: '/reports/tds',
          features: [
            'Monthly TDS deductions',
            'Employee-wise TDS',
            'Quarterly reports',
            'TDS certificates'
          ]
        }
      ]
    },
    {
      title: 'Payroll Reports',
      description: 'Comprehensive payroll and employee reports',
      reports: [
        {
          id: 'payroll-summary',
          title: 'Payroll Summary',
          description: 'Monthly payroll summaries and cost analysis',
          icon: DocumentTextIcon,
          color: 'indigo',
          route: '/reports/payroll-summary',
          features: [
            'Monthly payroll costs',
            'Department-wise breakdown',
            'Earnings vs Deductions',
            'Cost center analysis'
          ]
        },
        {
          id: 'employee-reports',
          title: 'Employee Reports',
          description: 'Individual employee salary and benefit reports',
          icon: UserGroupIcon,
          color: 'pink',
          route: '/reports/employee-reports',
          features: [
            'Individual salary slips',
            'Annual salary statements',
            'Benefit summaries',
            'Leave and attendance'
          ]
        }
      ]
    }
  ];

  const handleReportClick = (route) => {
    navigate(route);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'from-blue-50 to-indigo-50',
        border: 'border-blue-100',
        icon: 'bg-blue-100 text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700',
        text: 'text-blue-600'
      },
      green: {
        bg: 'from-green-50 to-emerald-50',
        border: 'border-green-100',
        icon: 'bg-green-100 text-green-600',
        button: 'bg-green-600 hover:bg-green-700',
        text: 'text-green-600'
      },
      purple: {
        bg: 'from-purple-50 to-violet-50',
        border: 'border-purple-100',
        icon: 'bg-purple-100 text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700',
        text: 'text-purple-600'
      },
      orange: {
        bg: 'from-orange-50 to-amber-50',
        border: 'border-orange-100',
        icon: 'bg-orange-100 text-orange-600',
        button: 'bg-orange-600 hover:bg-orange-700',
        text: 'text-orange-600'
      },
      indigo: {
        bg: 'from-indigo-50 to-blue-50',
        border: 'border-indigo-100',
        icon: 'bg-indigo-100 text-indigo-600',
        button: 'bg-indigo-600 hover:bg-indigo-700',
        text: 'text-indigo-600'
      },
      pink: {
        bg: 'from-pink-50 to-rose-50',
        border: 'border-pink-100',
        icon: 'bg-pink-100 text-pink-600',
        button: 'bg-pink-600 hover:bg-pink-700',
        text: 'text-pink-600'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div className="text-center bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
            Reports Center
          </h1>
          <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
            Generate comprehensive payroll and statutory compliance reports with real-time data insights
          </p>
        </div>

      {/* Report Categories */}
      {reportCategories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="space-y-6">
          <div className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-white/40">
            <h2 className="text-2xl font-semibold bg-gradient-to-r from-gray-800 to-indigo-800 bg-clip-text text-transparent">
              {category.title}
            </h2>
            <p className="text-gray-600 mt-2">{category.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.reports.map((report) => {
              const colors = getColorClasses(report.color);
              const IconComponent = report.icon;

              return (
                <div
                  key={report.id}
                  className={`bg-gradient-to-br ${colors.bg} rounded-2xl p-6 border ${colors.border} hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
                >
                  {/* Report Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-full ${colors.icon}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                  </div>

                  {/* Report Content */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {report.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {report.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Key Features:</h4>
                      <ul className="space-y-1">
                        {report.features.map((feature, index) => (
                          <li key={index} className="text-xs text-gray-600 flex items-center">
                            <div className={`w-1.5 h-1.5 rounded-full ${colors.text.replace('text-', 'bg-')} mr-2`}></div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => handleReportClick(report.route)}
                      className={`w-full ${colors.button} text-white py-2 px-4 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center justify-center group`}
                    >
                      Generate Report
                      <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Quick Stats */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-indigo-800 bg-clip-text text-transparent mb-4">
          Quick Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:shadow-md transition-all duration-300">
            <div className="text-2xl font-bold text-blue-600">6</div>
            <div className="text-sm text-gray-600">Report Types</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-md transition-all duration-300">
            <div className="text-2xl font-bold text-green-600">4</div>
            <div className="text-sm text-gray-600">Statutory Reports</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100 hover:shadow-md transition-all duration-300">
            <div className="text-2xl font-bold text-purple-600">2</div>
            <div className="text-sm text-gray-600">Payroll Reports</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 hover:shadow-md transition-all duration-300">
            <div className="text-2xl font-bold text-orange-600">Auto</div>
            <div className="text-sm text-gray-600">Data Sync</div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-gradient-to-r from-white/60 to-blue-50/60 backdrop-blur-sm rounded-2xl p-6 border border-white/40">
        <div className="text-center">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-indigo-800 bg-clip-text text-transparent mb-2">
            Need Help?
          </h3>
          <p className="text-gray-600 mb-4">
            All reports are generated from your payroll data automatically.
            Ensure your payroll is processed to view accurate reports.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
              Real-time Data
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-xs font-medium border border-green-200">
              Compliance Ready
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-violet-100 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
              Export Options
            </span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Reports;